<template>
  <Layout>
    <div class="material-list">
      <div class="page-header">
        <div>
          <h2>物料管理</h2>
          <p>管理广告物料资源</p>
        </div>
        <div class="header-actions">
          <input
            ref="folderInput"
            type="file"
            multiple
            webkitdirectory
            directory
            class="folder-input"
            @change="handleFolderSelected">
          <el-button @click="openFolderUpload">
            批量上传文件夹
          </el-button>
          <el-button type="primary" @click="uploadDialogVisible = true">
            上传物料
          </el-button>
        </div>
      </div>
      
      <!-- 筛选条件 -->
      <el-card shadow="never" class="filter-container">
        <el-form :inline="true" :model="filterForm" class="filter-form">
          <el-form-item label="物料类型">
            <el-select v-model="filterForm.type" placeholder="选择类型" clearable>
              <el-option label="图片" value="image"></el-option>
              <el-option label="视频" value="video"></el-option>
              <el-option label="DOCX文档" value="docx"></el-option>
              <el-option label="人群包" value="audience"></el-option>
            </el-select>
          </el-form-item>
          <el-form-item label="文件名">
            <el-input v-model="filterForm.keyword" placeholder="输入关键词"></el-input>
          </el-form-item>
          <el-form-item>
            <el-button type="primary" @click="handleFilter">查询</el-button>
            <el-button @click="resetFilter">重置</el-button>
          </el-form-item>
        </el-form>
      </el-card>
      
      <!-- 物料列表 -->
      <el-row :gutter="20" style="margin-top: 20px;">
        <el-col :span="24">
          <el-table
            v-loading="tableLoading"
            :data="pagedMaterials"
            border
            style="width: 100%">
            <el-table-column prop="id" label="ID" width="100"></el-table-column>
            <el-table-column prop="type" label="类型" width="100">
              <template slot-scope="scope">
                <el-tag :type="getTagType(scope.row.type)">
                  {{ getTypeName(scope.row.type) }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="filename" label="文件名" min-width="150"></el-table-column>
            <el-table-column label="预览" width="150">
              <template slot-scope="scope">
                <div v-if="scope.row.type === 'image'">
                  <el-image 
                    style="width: 80px; height: 45px" 
                    :src="getPreviewUrl(scope.row)" 
                    fit="cover"
                    :preview-src-list="[getPreviewUrl(scope.row)]">
                  </el-image>
                </div>
                <div v-else-if="scope.row.type === 'video'">
                  <div class="video-preview-card">
                    <el-image
                      style="width: 80px; height: 45px"
                      :src="getPreviewUrl(scope.row, 'poster')"
                      fit="cover">
                    </el-image>
                    <el-button size="mini" @click="previewVideo(scope.row)">预览视频</el-button>
                  </div>
                </div>
                <div v-else-if="scope.row.type === 'docx'" class="doc-preview-card">
                  <el-image
                    v-if="scope.row.cover_url"
                    style="width: 80px; height: 45px"
                    :src="getPreviewUrl(scope.row, 'cover')"
                    fit="cover">
                  </el-image>
                  <div v-else class="doc-preview-fallback">DOCX</div>
                </div>
                <div v-else-if="scope.row.type === 'audience'">
                  <el-tooltip content="查看CSV文件内容" placement="top">
                    <el-button size="mini" type="info" @click="viewFullUrl(scope.row)">查看数据</el-button>
                  </el-tooltip>
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="create_time" label="上传时间" width="180"></el-table-column>
            <el-table-column label="操作" width="220">
              <template slot-scope="scope">
                <el-button 
                  size="mini" 
                  type="success" 
                  @click="copyUrl(scope.row)">
                  复制链接
                </el-button>
                <el-button 
                  size="mini" 
                  type="primary" 
                  @click="handleMaterialAction(scope.row)">
                  {{ scope.row.type === 'audience' ? '查看数据' : '下载' }}
                </el-button>
              </template>
            </el-table-column>
          </el-table>

          <div class="pagination-container">
            <el-pagination
              @size-change="handleSizeChange"
              @current-change="handleCurrentChange"
              :current-page="currentPage"
              :page-sizes="[10, 30, 50, 100]"
              :page-size="pageSize"
              layout="total, sizes, prev, pager, next, jumper"
              :total="totalMaterials">
            </el-pagination>
          </div>
        </el-col>
      </el-row>
      
      <!-- 上传对话框 -->
      <el-dialog
        title="上传物料"
        :visible.sync="uploadDialogVisible"
        width="500px">
        <el-form :model="uploadForm" label-width="80px">
          <el-form-item label="物料类型">
            <el-radio-group v-model="uploadForm.type">
              <el-radio label="image">图片</el-radio>
              <el-radio label="video">视频</el-radio>
              <el-radio label="docx">DOCX文档</el-radio>
              <el-radio label="audience">人群包</el-radio>
            </el-radio-group>
          </el-form-item>
          <el-form-item label="上传文件">
            <el-upload
              class="upload"
              drag
              action="#"
              :http-request="handleFileUpload"
              :before-upload="beforeUpload"
              :file-list="fileList"
              :limit="1">
              <i class="el-icon-upload"></i>
              <div class="el-upload__text">将文件拖到此处，或<em>点击上传</em></div>
              <div class="el-upload__tip" slot="tip">
                <span v-if="uploadForm.type === 'image'">只能上传JPG/PNG图片文件</span>
                <span v-else-if="uploadForm.type === 'video'">只能上传MP4视频文件</span>
                <span v-else-if="uploadForm.type === 'docx'">只能上传DOCX图文文档</span>
                <span v-else-if="uploadForm.type === 'audience'">只能上传CSV或JSON格式的人群包文件</span>
              </div>
            </el-upload>
          </el-form-item>
        </el-form>
        <span slot="footer" class="dialog-footer">
          <el-button @click="uploadDialogVisible = false">取 消</el-button>
          <el-button type="primary" @click="submitUpload" :disabled="!hasFileToUpload">确 定</el-button>
        </span>
      </el-dialog>
      
      <!-- 查看人群包内容对话框 -->
      <el-dialog
        title="人群包数据"
        :visible.sync="urlDialogVisible"
        width="600px">
        <div class="url-container">
          <el-input type="textarea" :rows="4" v-model="selectedMaterialUrl" readonly></el-input>
        </div>
      </el-dialog>
      
      <!-- 视频预览对话框 -->
      <el-dialog
        title="视频预览"
        :visible.sync="videoDialogVisible"
        width="70%">
        <div class="video-container" v-if="selectedMaterial && selectedMaterial.url">
          <video controls style="width: 100%; max-height: 70vh">
            <source :src="getPreviewUrl(selectedMaterial)" type="video/mp4">
            您的浏览器不支持视频播放
          </video>
        </div>
      </el-dialog>
    </div>
  </Layout>
</template>

<script>
import Layout from '@/components/Layout.vue'
import { mapState } from 'vuex'

export default {
  name: 'MaterialList',
  components: {
    Layout
  },
  data() {
    return {
      filterForm: {
        type: '',
        keyword: ''
      },
      uploadDialogVisible: false,
      urlDialogVisible: false,
      videoDialogVisible: false,
      uploadForm: {
        type: 'image'
      },
      fileList: [],
      uploadFile: null,
      selectedMaterial: null,
      selectedMaterialUrl: '',
      folderUploading: false,
      currentPage: 1,
      pageSize: 30
    }
  },
  computed: {
    ...mapState(['materials', 'loading', 'audiencePackages']),
    tableLoading() {
      return this.loading || this.folderUploading
    },
    filteredMaterials() {
      // 合并物料和人群包数据
      let result = [...this.materials]
      
      // 添加人群包数据（如果不在物料列表中）
      const materialIds = new Set(result.map(m => m.id))
      const audiencePackagesToAdd = this.audiencePackages.filter(pkg => !materialIds.has(pkg.id))
      result = [...result, ...audiencePackagesToAdd]
      
      // 按类型筛选
      if (this.filterForm.type) {
        result = result.filter(item => item.type === this.filterForm.type)
      }
      
      // 按关键词筛选
      if (this.filterForm.keyword) {
        const keyword = this.filterForm.keyword.toLowerCase()
        result = result.filter(item => 
          (item.filename && item.filename.toLowerCase().includes(keyword))
        )
      }
      
      // 按上传时间排序
      return result.sort((a, b) => new Date(b.create_time) - new Date(a.create_time))
    },
    pagedMaterials() {
      const startIndex = (this.currentPage - 1) * this.pageSize
      const endIndex = startIndex + this.pageSize
      return this.filteredMaterials.slice(startIndex, endIndex)
    },
    totalMaterials() {
      return this.filteredMaterials.length
    },
    hasFileToUpload() {
      return this.uploadFile !== null
    }
  },
  methods: {
    parseMaterialTime(timeText) {
      if (!timeText) {
        return null
      }
      const normalized = String(timeText).replace(/-/g, '/')
      const parsed = new Date(normalized)
      return Number.isNaN(parsed.getTime()) ? null : parsed
    },
    isArchivedMaterial(material) {
      if (!material || !material.create_time) {
        return false
      }
      const createdAt = this.parseMaterialTime(material.create_time)
      if (!createdAt) {
        return false
      }
      const now = new Date()
      const archivedAt = new Date(createdAt)
      archivedAt.setMonth(archivedAt.getMonth() + 6)
      return now >= archivedAt
    },
    showArchivedMaterialMessage() {
      this.$message.warning('物料已归档')
    },
    openFolderUpload() {
      this.$refs.folderInput.click()
    },
    handleFilter() {
      this.currentPage = 1
    },
    resetFilter() {
      this.filterForm = {
        type: '',
        keyword: ''
      }
      this.currentPage = 1
    },
    handleSizeChange(val) {
      this.pageSize = val
      this.currentPage = 1
    },
    handleCurrentChange(val) {
      this.currentPage = val
    },
    beforeUpload(file) {
      console.log('File info:', file.type, file.size, file.name)
      const lowerName = file.name.toLowerCase()
      const isImage = file.type.startsWith('image/')
      const isVideo = file.type.startsWith('video/')
      const isDocx = lowerName.endsWith('.docx') || file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      const isCSV = lowerName.endsWith('.csv')
      const isJSON = lowerName.endsWith('.json')
      
      if (!this.uploadForm.type) {
        this.$message.error('请先选择物料类型!')
        return false
      }
      
      if (this.uploadForm.type === 'image' && !isImage) {
        this.$message.error('请上传图片文件!')
        return false
      }
      
      if (this.uploadForm.type === 'video' && !isVideo) {
        this.$message.error('请上传视频文件!')
        return false
      }

      if (this.uploadForm.type === 'docx' && !isDocx) {
        this.$message.error('请上传DOCX文档文件!')
        return false
      }

      if (this.uploadForm.type === 'audience' && !(isCSV || isJSON)) {
        this.$message.error('请上传CSV或JSON格式的人群包文件!')
        return false
      }
      
      const isLt100M = file.size / 1024 / 1024 < 100;
      
      // 保存文件到组件状态
      this.uploadFile = file
      return true
    },
    handleFileUpload(params) {
      // 阻止默认上传
      this.uploadFile = params.file
      this.fileList = [{ name: params.file.name, url: '' }]
    },
    async submitUpload() {
      if (!this.uploadFile) {
        this.$message.warning('请选择要上传的文件')
        return
      }
      
      const result = await this.$store.dispatch('uploadMaterial', {
        file: this.uploadFile,
        type: this.uploadForm.type
      })
      
      if (result) {
        this.$message.success('上传成功')
        this.uploadDialogVisible = false
        this.uploadFile = null
        this.fileList = []
      } else {
        this.$message.error('上传失败')
      }
    },
    inferMaterialType(file) {
      const lowerName = (file.name || '').toLowerCase()
      const mimeType = file.type || ''
      if (mimeType.startsWith('image/') || /\.(jpg|jpeg|png|webp|gif)$/.test(lowerName)) {
        return 'image'
      }
      if (mimeType.startsWith('video/') || /\.(mp4|mov|avi|m4v)$/.test(lowerName)) {
        return 'video'
      }
      if (/\.docx$/.test(lowerName)) {
        return 'docx'
      }
      if (/\.(csv|json)$/.test(lowerName)) {
        return 'audience'
      }
      return ''
    },
    async handleFolderSelected(event) {
      const files = Array.from((event.target && event.target.files) || [])
      const uploadableFiles = files.filter(file => {
        const relativePath = file.webkitRelativePath || file.name || ''
        if (!relativePath || /(^|\/)\./.test(relativePath) || /(^|\/)~/.test(relativePath)) {
          return false
        }
        return !!this.inferMaterialType(file)
      })

      if (!uploadableFiles.length) {
        this.$message.warning('所选文件夹中没有可识别的图片、视频、DOCX或人群包文件')
        event.target.value = ''
        return
      }

      this.folderUploading = true
      let successCount = 0
      const failedFiles = []
      const concurrency = 10
      let nextIndex = 0
      const worker = async () => {
        while (nextIndex < uploadableFiles.length) {
          const currentIndex = nextIndex
          nextIndex += 1
          const file = uploadableFiles[currentIndex]
          const type = this.inferMaterialType(file)
          const relativePath = file.webkitRelativePath || file.name
          try {
            const result = await this.$store.dispatch('uploadMaterial', {
              file,
              type,
              relativePath,
              batchMode: true
            })
            if (result) {
              successCount += 1
            } else {
              failedFiles.push(file.name)
            }
          } catch (error) {
            failedFiles.push(file.name)
          }
        }
      }
      const workerCount = Math.min(concurrency, uploadableFiles.length)
      try {
        await Promise.all(Array.from({ length: workerCount }, () => worker()))
        await Promise.all([
          this.$store.dispatch('fetchMaterials'),
          this.$store.dispatch('fetchAudiencePackages'),
          this.$store.dispatch('fetchCampaignActivities')
        ])
      } finally {
        this.folderUploading = false
        event.target.value = ''
      }

      if (failedFiles.length) {
        this.$message.warning(`批量上传完成，成功 ${successCount} 个，失败 ${failedFiles.length} 个`)
      } else {
        this.$message.success(`批量上传完成，共上传 ${successCount} 个文件`)
      }
    },
    copyUrl(material) {
      if (this.isArchivedMaterial(material)) {
        this.showArchivedMaterialMessage()
        return
      }
      const url = material && material.url
      if (!url) {
        this.$message.error('链接不存在')
        return
      }
      // 创建一个临时的textarea元素来复制文本
      const textarea = document.createElement('textarea')
      textarea.value = url
      document.body.appendChild(textarea)
      textarea.select()
      
      try {
        document.execCommand('copy')
        this.$message.success('链接已复制到剪贴板')
      } catch (err) {
        this.$message.error('复制失败')
      }
      
      document.body.removeChild(textarea)
    },
    handleMaterialAction(material) {
      if (this.isArchivedMaterial(material)) {
        this.showArchivedMaterialMessage()
        return
      }
      if (material.type === 'audience') {
        this.viewFullUrl(material)
        return
      }
      this.downloadMaterial(material)
    },
    viewFullUrl(material) {
      this.selectedMaterialUrl = material.url
      this.urlDialogVisible = true
    },
    getPreviewUrl(material, asset = 'source') {
      if (!material || !material.id) {
        return ''
      }
      if (asset === 'cover' && !material.cover_url) {
        return ''
      }
      const customerId = localStorage.getItem('activeCustomerId') || ''
      const params = new URLSearchParams()
      if (customerId) {
        params.set('customer_id', customerId)
      }
      if (asset && asset !== 'source') {
        params.set('asset', asset)
      }
      return `/api/materials/${encodeURIComponent(material.id)}/preview?${params.toString()}`
    },
    downloadMaterial(material) {
      if (!material || !material.id) {
        this.$message.error('物料不存在，无法下载')
        return
      }
      const customerId = localStorage.getItem('activeCustomerId') || ''
      const params = new URLSearchParams()
      if (customerId) {
        params.set('customer_id', customerId)
      }
      params.set('_t', Date.now())
      const url = `/api/materials/${encodeURIComponent(material.id)}/download?${params.toString()}`
      const link = document.createElement('a')
      link.href = url
      link.style.display = 'none'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
    previewVideo(material) {
      this.selectedMaterial = material
      this.videoDialogVisible = true
    },
    getTagType(type) {
      const types = {
        'image': 'success',
        'video': 'warning',
        'docx': 'primary',
        'audience': 'info'
      }
      return types[type] || 'info'
    },
    getTypeName(type) {
      const names = {
        'image': '图片',
        'video': '视频',
        'docx': 'DOCX文档',
        'audience': '人群包'
      }
      return names[type] || '未知'
    }
  },
  created() {
    this.$store.dispatch('fetchMaterials')
    this.$store.dispatch('fetchAudiencePackages')
  }
}
</script>

<style scoped>
.material-list {
  padding: 20px 0;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.header-actions {
  display: flex;
  gap: 12px;
}

.page-header h2 {
  margin: 0;
  font-weight: normal;
}

.page-header p {
  margin: 5px 0 0;
  color: #909399;
}

.filter-container {
  margin-bottom: 20px;
}

.filter-form {
  display: flex;
  align-items: center;
}

.pagination-container {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
}

.material-card {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.material-image {
  height: 120px;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
}

.material-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.upload {
  width: 100%;
}

.url-container {
  word-break: break-all;
}

.folder-input {
  display: none;
}

.doc-preview-card {
  display: flex;
  align-items: center;
}

.doc-preview-fallback {
  width: 80px;
  height: 45px;
  border-radius: 8px;
  background: linear-gradient(135deg, #2f6bff, #6a8bff);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
}

.video-preview-card {
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: flex-start;
}
</style>
